import { useState } from 'react';
import { Anfrage, anfragenApi } from '@/lib/api';
import { Check, CircleAlert, X } from 'lucide-react';
import {
  Button,
  Callout,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SegmentedControl,
  toast,
} from '@elbklar/ui';
import { TextareaField } from '@/components/form/Felder';

interface AnfrageCloseModalProps {
  anfrage: Anfrage;
  customerName?: string;
  onClose: () => void;
  onSave: () => void;
}

export function AnfrageCloseModal({
  anfrage,
  customerName,
  onClose,
  onSave,
}: AnfrageCloseModalProps) {
  const [won, setWon] = useState(true);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!won && !reason.trim()) {
      setError('Grund für den Verlust fehlt');
      return;
    }

    try {
      setLoading(true);
      await anfragenApi.transition(
        anfrage.id,
        won ? 'GEWONNEN' : 'VERLOREN',
        anfrage.version,
        reason || undefined,
      );
      toast.success(
        won ? 'Anfrage als gewonnen markiert' : 'Anfrage als verloren markiert',
      );
      onSave();
    } catch (err: unknown) {
      const axiosError = err as { response?: { status?: number } };
      if (axiosError.response?.status === 409) {
        setError('Die Anfrage wurde inzwischen geändert. Seite neu laden.');
      } else if (axiosError.response?.status === 422) {
        setError('Dieser Statuswechsel ist nicht erlaubt');
      } else {
        setError('Anfrage ließ sich nicht abschließen');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const title = anfrage.kundenwunsch || anfrage.sales_nachricht || customerName || 'Anfrage';

  return (
    <Dialog open onOpenChange={(offen) => !offen && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Anfrage abschließen</DialogTitle>
          <DialogDescription>
            Anfrage: <span className="font-medium text-foreground">{title}</span>
          </DialogDescription>
        </DialogHeader>

        {error && <Callout tone="danger" icon={CircleAlert} title={error} boxed />}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Won/Lost Selection */}
          <SegmentedControl
            label="Ergebnis"
            className="w-full [&>button]:flex-1 [&>button]:justify-center"
            value={won ? 'gewonnen' : 'verloren'}
            onChange={(v) => setWon(v === 'gewonnen')}
            items={[
              { value: 'gewonnen', label: 'Gewonnen', icon: Check },
              { value: 'verloren', label: 'Verloren', icon: X },
            ]}
          />

          {/* Reason (required for lost) */}
          <TextareaField
            id="reason"
            label={won ? 'Notiz (optional)' : 'Grund für Verlust'}
            required={!won}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={won ? 'Anmerkungen' : 'Warum die Anfrage verloren ist'}
          />

          {/* Actions */}
          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" variant={won ? 'primary' : 'danger'} loading={loading}>
              {won ? 'Als gewonnen markieren' : 'Als verloren markieren'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
